// Intermediate "Confirm Appointment" screen — between the user clicking
// Book Fitting and the actual POST /bookings write to SF.
//
// Shows a 10-minute hold notice + everything the user has chosen, with
// inline Change links to jump back to a particular field. On Confirm we
// fire the real SF write (delegated via onConfirm callback).
//
// Design comp reference: Screenshot 2026-05-25 6:18:49.

import styles from '../styles/widget.module.css';
import { formatSlotTime } from '../utils/datetime';
import type { BookingState } from '../state/machine';
import type { BookingEditField } from '../utils/bookingFlow';

interface ConfirmAppointmentProps {
  state: BookingState;
  onConfirm: () => void;
  onEditField: (field: BookingEditField) => void;
}

export function ConfirmAppointment({ state, onConfirm, onEditField }: ConfirmAppointmentProps) {
  return (
    <div className={styles.confirmRoot}>
      <h2 className={styles.confirmTitle}>Confirm Your Appointment</h2>
      <p className={styles.confirmHoldNotice}>
        Your Appointment is being held for the next <strong>9:59 minutes</strong>
      </p>

      <div className={styles.confirmGrid}>
        <div className={styles.confirmSection}>
          <h4>Your Information</h4>
          <p><strong>Name:</strong> {state.fullName}</p>
          <p><strong>Email:</strong> {state.email}</p>
        </div>

        <div className={styles.confirmDetails}>
          <div className={styles.confirmSection}>
            <h4>
              Your Fitting Details
            </h4>
            <p className={styles.rowLabel}>
              <strong>Location</strong>{' '}
              <button
                className={styles.changeLink}
                data-cc-track="change_link"
                data-cc-track-id="change_location"
                onClick={() => onEditField('location')}
              >
                Change
              </button>
            </p>
            <p><strong>{state.selectedLocation?.name}</strong></p>
            {state.selectedLocation?.address && <p>{state.selectedLocation.address}</p>}

            <p className={styles.rowLabel}>
              <strong>Date &amp; Time</strong>{' '}
              <button
                className={styles.changeLink}
                data-cc-track="change_link"
                data-cc-track-id="change_date"
                onClick={() => onEditField('dateTime')}
              >
                Change
              </button>
            </p>
            <p>{state.selectedDate}</p>
            <p>{state.selectedTime ? formatSlotTime(state.selectedTime) : ''}</p>
          </div>

          <div className={styles.confirmSection}>
            <p>
              <strong>Service</strong>{' '}
              <button
                className={styles.changeLink}
                data-cc-track="change_link"
                data-cc-track-id="change_fitting_type"
                onClick={() => onEditField('fittingType')}
              >
                Change
              </button>
            </p>
            <p>{state.selectedFittingType?.displayName ?? state.selectedFittingType?.name}</p>
            <p>{state.selectedFittingType?.durationLabel}</p>

            <p className={styles.rowLabel}>
              <strong>Fitter</strong>
            </p>
            <p>{state.selectedSlot?.fitterName ?? 'Any Fitter'}</p>

            <p className={styles.rowLabel}>
              <strong>Pricing</strong>
            </p>
            <p>{state.selectedFittingType?.pricingLabel}</p>
            <div className={styles.pricingDisclaimer}>
              <span>You are under no obligation to buy.</span>
              <a href='#' className={styles.classicGreenLink}>See Terms and Conditions</a></div>
          </div>
        </div>
      </div>

      {state.submitError && <div className={styles.error}>{state.submitError}</div>}

      <div className={styles.confirmFooter}>
        <button
          className={styles.button}
          disabled={state.submitting}
          data-cc-track="cta"
          data-cc-track-id="confirm_appointment"
          onClick={onConfirm}
        >
          {state.submitting ? (
            <>
              <span className={styles.spinner} />
              Booking…
            </>
          ) : (
            'Confirm Appointment'
          )}
        </button>
      </div>
    </div>
  );
}
